import { supabase } from '../utils/supabase';
import { Paciente } from '../types';

export interface PacienteSearchResponse {
  success: boolean;
  message: string;
  data?: Paciente[];
  error?: Error;
}

export interface PacienteResponse {
  success: boolean;
  message: string;
  data?: Paciente;
  error?: Error;
}

export interface PacientesListResponse {
  success: boolean;
  message: string;
  data?: Paciente[];
  count?: number;
  error?: Error;
}

export interface PacienteDeleteResponse {
  success: boolean;
  message: string;
  error?: Error;
}

export class PacienteService {
  /**
   * Search patients by name or CPF for a specific organization
   */
  static async searchPacientes(
    organizationId: string,
    searchTerm: string,
    limit: number = 10
  ): Promise<PacienteSearchResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    if (!searchTerm?.trim()) {
      return {
        success: true,
        message: 'No search term provided',
        data: []
      };
    }

    try {
      const { data: pacientes, error } = await supabase
        .from('pacientes')
        .select('*')
        .eq('organization_id', organizationId)
        .or(`nome.ilike.%${searchTerm.trim()}%,cpf.ilike.%${searchTerm.trim()}%`)
        .order('nome')
        .limit(limit);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Pacientes encontrados com sucesso',
        data: pacientes || []
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get paginated list of patients for an organization
   */
  static async getPacientes(
    organizationId: string,
    page: number = 0,
    limit: number = 10,
    nome?: string
  ): Promise<PacientesListResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      let supabaseQuery = supabase
        .from('pacientes')
        .select('*', { count: 'exact' })
        .eq('organization_id', organizationId)
        .range(page * limit, (page + 1) * limit - 1)
        .order('id', { ascending: false });

      // Add nome filter if it's defined
      if (nome !== undefined) {
        supabaseQuery = supabaseQuery.ilike('nome', `%${nome}%`);
      }

      const { data, count, error } = await supabaseQuery;

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Pacientes carregados com sucesso',
        data: data || [],
        count: count || 0
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get a specific patient by ID
   */
  static async getPacienteById(
    pacienteId: string,
    organizationId: string
  ): Promise<PacienteResponse> {
    if (!pacienteId) {
      return {
        success: false,
        message: 'Patient ID is required',
        error: new Error('Patient ID is required')
      };
    }

    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { data: paciente, error } = await supabase
        .from('pacientes')
        .select('*')
        .eq('id', pacienteId)
        .eq('organization_id', organizationId)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Paciente encontrado com sucesso',
        data: paciente
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Create or update a patient
   */
  static async upsertPaciente(
    organizationId: string,
    pacienteData: Partial<Paciente>
  ): Promise<PacienteResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      // Ensure organization_id is set on the data
      const bodyWithOrg = {
        ...pacienteData,
        organization_id: organizationId
      };

      // Clean up fields that shouldn't be in upsert
      if (!bodyWithOrg.id) delete bodyWithOrg.id;
      delete bodyWithOrg.created_at;
      delete bodyWithOrg.updated_at;

      const { data, error } = await supabase
        .from('pacientes')
        .upsert(bodyWithOrg)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: pacienteData.id
          ? 'Paciente atualizado com sucesso'
          : 'Paciente criado com sucesso',
        data: data
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Delete a patient by ID
   */
  static async deletePaciente(
    pacienteId: string,
    organizationId: string
  ): Promise<PacienteDeleteResponse> {
    if (!pacienteId) {
      return {
        success: false,
        message: 'Patient ID is required',
        error: new Error('Patient ID is required')
      };
    }

    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { error } = await supabase
        .from('pacientes')
        .delete()
        .eq('id', pacienteId)
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Paciente removido com sucesso'
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get patient count for dashboard stats
   */
  static async getPacienteCount(
    organizationId: string,
    nome?: string
  ): Promise<{ success: boolean; message: string; count?: number; error?: Error }> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      let query = supabase
        .from('pacientes')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId);

      if (nome !== undefined) {
        query = query.ilike('nome', `%${nome}%`);
      }

      const { count, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Contagem de pacientes realizada com sucesso',
        count: count || 0
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }
}
